import React from 'react';
import { createPortal } from 'react-dom';
import * as Dialog from '@radix-ui/react-dialog';
import PaymentDialogClose from './PaymentDialogClose';
import useBottomSheet from './useBottomSheet';
import { ArrowLeft } from 'lucide-react';

// The footer host of the open dialog: `undefined` outside any dialog (the content renders its actions in place),
// `null` while the dialog's footer is mounting, an element once there is one to render into.
const DialogFooterContext = React.createContext<HTMLElement | null | undefined>(undefined);

/** The dialog's non-scrolling footer (#181 round 2): the content's primary action rendered below the body scroller,
 *  so it is on screen whatever the body's scroll position and above the browser's own bottom bars. Outside a
 *  dialog the children render where they are written. */
export function DialogFooter({ children }: { children: React.ReactNode }) {
  const host = React.useContext(DialogFooterContext);
  if (host === undefined) return <>{children}</>;
  return host ? createPortal(children, host) : null;
}

/** Shared, focus-managed shell for the public bill's portaled surfaces: a centered dialog on desktop, a draggable bottom sheet on phones. */
export default function PublicBillDialog({ open, onClose, onBack, title, children, className = '', contentHasClose = false, busy = false, container }: {
  open: boolean;
  onClose: () => void;
  onBack?: () => void;
  title: string;
  children: React.ReactNode;
  className?: string;
  contentHasClose?: boolean;
  busy?: boolean;
  container?: HTMLElement | null;
}) {
  const previousFocus = React.useRef<HTMLElement | null>(null);
  const heading = React.useRef<HTMLHeadingElement | null>(null);
  const [sheet, setSheet] = React.useState<HTMLDivElement | null>(null);
  const [backdrop, setBackdrop] = React.useState<HTMLDivElement | null>(null);
  const [footer, setFooter] = React.useState<HTMLDivElement | null>(null);
  // Contained surfaces (the receipt viewer) stay put; everything else gets the sheet physics on phone
  // widths, with the backdrop following the sheet frame by frame. The hook writes the sheet's `data-detent`
  // itself, so a gesture never re-renders this shell.
  useBottomSheet(sheet, { enabled: open && !container, locked: busy, backdrop, onDismiss: onClose });
  React.useLayoutEffect(() => {
    if (open) previousFocus.current = document.activeElement as HTMLElement;
  }, [open]);
  React.useEffect(() => {
    if (open && !contentHasClose) heading.current?.focus();
  }, [open, title, contentHasClose]);
  return (
    <Dialog.Root open={open} onOpenChange={(next) => { if (!next && !busy) onClose(); }}>
      <Dialog.Portal container={container}>
        <Dialog.Overlay ref={setBackdrop} className={`bill-dialog-overlay${container ? ' bill-dialog-overlay--contained' : ''}`} />
        <Dialog.Content
          ref={setSheet}
          className={`vs-design bill-dialog${container ? " bill-dialog--contained" : ""} ${className}`}
          aria-describedby={undefined}
          onOpenAutoFocus={(event) => { if (contentHasClose) event.preventDefault(); }}
          onCloseAutoFocus={(event) => { event.preventDefault(); previousFocus.current?.focus(); }}
          onEscapeKeyDown={(event) => { if (busy || contentHasClose) event.preventDefault(); }}
          onInteractOutside={(event) => { if (busy) event.preventDefault(); }}
        >
          {/* The grab handle and the heading always drag the sheet, whatever the content's scroll position. */}
          {!container && <div className="bill-sheet-grip" aria-hidden="true" data-sheet-handle=""><span /></div>}
          {onBack && <button type="button" className="bill-dialog-back" aria-label="Back to payment" onClick={onBack} disabled={busy}><ArrowLeft size={18} aria-hidden="true" /> Back</button>}
          <Dialog.Title ref={heading} tabIndex={-1} className={contentHasClose ? "sr-only" : "bill-dialog-heading"} data-sheet-handle="">{title}</Dialog.Title>
          {!contentHasClose && <PaymentDialogClose onClose={onClose} disabled={busy} />}
          {/* The body is the one scroller; the footer below it never scrolls, and keeps clear of the browser's
              own bottom bars (#181 round 2: iOS 26 Safari lays the page out under its floating toolbar). */}
          <DialogFooterContext.Provider value={footer}>
            <div className="bill-dialog-body">{children}</div>
          </DialogFooterContext.Provider>
          <div className="bill-dialog-footer" ref={setFooter} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
